import { LockOutlined } from '@ant-design/icons';
import { Alert, Button, Form, Input, Spin, Typography } from 'antd';
import type React from 'react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import AuthCard from '../components/AuthCard';
import api from '../config/api';
import { baseLanguage } from '../i18n/locale';

const { Text, Paragraph } = Typography;

type Stage = 'checking' | 'invalid' | 'form' | 'done';

/**
 * A kiszolgáló egyetlen mondata minden rossz tokenre — csak ERRE váltunk a
 * „lejárt link" állapotba. Más 400 (pl. a kiszolgáló jelszó-szabálya) az
 * űrlapot hagyja, hibaüzenettel: az nem a hivatkozás baja.
 */
const INVALID_TOKEN_MESSAGE = 'This link is invalid or has expired.';
const PASSWORD_MAX = 128;

/**
 * Elfelejtett jelszó, 2. lépés (#109): az új jelszó beállítása a levélben
 * kapott hivatkozásról.
 *
 * A token az URL FRAGMENTJÉBEN érkezik (`/reset-password#token=…`), nem a
 * query stringben. A fragmentet a böngésző sosem küldi el a kiszolgálónak és
 * Refererként sem, tehát a betöltés nem írja a 60 percig élő tokent sem a
 * Traefik, sem az admin-nginx access logjába (QA-átvilágítás, CWE-598). A
 * query stringben érkező tokent szándékosan NEM fogadjuk el — az már a
 * naplóban lenne, mire ideér.
 *
 * A token a fragmentből egyetlen olvasással a memóriába kerül, és az URL-ből
 * azonnal eltűnik (`replace` navigáció = `history.replaceState`), hogy ne
 * maradjon a böngésző előzményeiben. Amíg az oldal él, a dokumentumon
 * `no-referrer` áll — a panel Google Fonts-ot tölt, és bár a böngésző
 * alapból csak az origint küldi idegen helyre, ez a biztonsági öv nem kerül
 * semmibe.
 *
 * A hivatkozás betöltéskor ÉS beküldéskor is ellenőrződik. A rossz token
 * egyetlen, általános üzenetet kap: nem tudni, lejárt-e, elhasznált-e vagy
 * soha nem létezett. Siker után NINCS automatikus belépés (OWASP) — a
 * bejelentkezés maga bizonyítja, hogy az új jelszó működik.
 */
const ResetPassword: React.FC = () => {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const [token] = useState(
    () => new URLSearchParams(location.hash.replace(/^#/, '')).get('token') ?? ''
  );
  const [stage, setStage] = useState<Stage>(token ? 'checking' : 'invalid');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const meta = document.createElement('meta');
    meta.name = 'referrer';
    meta.content = 'no-referrer';
    document.head.appendChild(meta);
    return () => {
      meta.remove();
    };
  }, []);

  useEffect(() => {
    // A fragmentet (és egy elutasított, query stringben érkező tokent is)
    // azonnal töröljük a címsorból — az előzményekben sincs keresnivalója.
    if (location.hash || location.search) {
      navigate(location.pathname, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    // POST, a token a törzsben — a query string a kiszolgáló naplójába kerülne.
    api
      .post('/auth/reset-password/validate', { token })
      .then((r) => {
        if (!cancelled) setStage(r.data?.valid ? 'form' : 'invalid');
      })
      .catch(() => {
        if (!cancelled) setStage('invalid');
      });
    return () => {
      cancelled = true;
    };
  }, [token]);

  const handleSubmit = async ({ password }: { password: string; confirm: string }) => {
    setSubmitting(true);
    setError(null);
    try {
      await api.post('/auth/reset-password', {
        token,
        password,
        lang: baseLanguage(i18n.language),
      });
      setStage('done');
    } catch (e: unknown) {
      const response = (e as { response?: { status?: number; data?: { message?: unknown } } })
        ?.response;
      const status = response?.status;
      if (status === 400 && response?.data?.message === INVALID_TOKEN_MESSAGE) {
        setStage('invalid');
      } else {
        setError(status === 429 ? t('forgotPassword.tooMany') : t('resetPassword.failed'));
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AuthCard eyebrow={t('resetPassword.title')}>
      {stage === 'checking' && (
        <div style={{ textAlign: 'center' }}>
          <Spin />
          <Paragraph style={{ marginTop: 12, color: 'var(--ink-soft)' }}>
            {t('resetPassword.checking')}
          </Paragraph>
        </div>
      )}

      {stage === 'invalid' && (
        <>
          <Alert
            type="warning"
            showIcon
            message={t('resetPassword.invalidTitle')}
            description={t('resetPassword.invalidBody')}
          />
          <div style={{ textAlign: 'center' }}>
            <Link to="/forgot-password" style={{ color: 'var(--vermilion)', fontWeight: 500 }}>
              {t('resetPassword.requestNew')}
            </Link>
          </div>
        </>
      )}

      {stage === 'form' && (
        <>
          {error && <Alert type="error" showIcon message={error} />}
          <Form
            name="reset-password"
            onFinish={handleSubmit}
            layout="vertical"
            size="large"
            requiredMark={false}
          >
            <Form.Item
              name="password"
              label={t('resetPassword.password')}
              rules={[
                { required: true, message: t('resetPassword.passwordRequired') },
                { min: 6, message: t('resetPassword.passwordMin') },
                { max: PASSWORD_MAX, message: t('resetPassword.passwordMax') },
              ]}
            >
              <Input.Password
                autoComplete="new-password"
                prefix={<LockOutlined style={{ color: 'var(--ink-faded)' }} />}
                placeholder={t('resetPassword.passwordMin')}
              />
            </Form.Item>
            <Form.Item
              name="confirm"
              label={t('resetPassword.confirm')}
              dependencies={['password']}
              rules={[
                { required: true, message: t('resetPassword.confirmRequired') },
                ({ getFieldValue }) => ({
                  validator: (_rule, value: string) =>
                    !value || getFieldValue('password') === value
                      ? Promise.resolve()
                      : Promise.reject(new Error(t('resetPassword.mismatch'))),
                }),
              ]}
            >
              <Input.Password
                autoComplete="new-password"
                prefix={<LockOutlined style={{ color: 'var(--ink-faded)' }} />}
                placeholder={t('resetPassword.confirm')}
              />
            </Form.Item>
            <Form.Item style={{ marginBottom: 0 }}>
              <Button type="primary" htmlType="submit" loading={submitting} block>
                {t('resetPassword.submit')}
              </Button>
            </Form.Item>
          </Form>
        </>
      )}

      {stage === 'done' && (
        <>
          <Alert
            type="success"
            showIcon
            message={t('resetPassword.doneTitle')}
            description={t('resetPassword.doneBody')}
          />
          <div style={{ textAlign: 'center' }}>
            <Link to="/login" style={{ color: 'var(--vermilion)', fontWeight: 500 }}>
              {t('resetPassword.goToLogin')}
            </Link>
          </div>
        </>
      )}

      {stage === 'form' && (
        <div style={{ textAlign: 'center' }}>
          <Text style={{ color: 'var(--ink-soft)' }}>
            <Link to="/login" style={{ color: 'var(--vermilion)', fontWeight: 500 }}>
              {t('forgotPassword.backToLogin')}
            </Link>
          </Text>
        </div>
      )}
    </AuthCard>
  );
};

export default ResetPassword;
